import React, { useCallback, useRef, useState } from "react";
import {
  Container,
  Text,
  Title,
  Box,
  Group,
  Grid,
  SimpleGrid,
  Tabs,
} from "@mantine/core";
import { ResponsiveLine, Line } from "@nivo/line";

import { Page } from "../layout/page";

import LineChart from "../components/chartWidget/LineChart";
import PieChart from "../components/chartWidget/PieChart";
import BarChart from "../components/chartWidget/BarChart";
import TreemapChart from "../components/chartWidget/TreemapChart";
import CalendarChart from "../components/chartWidget/CalendarChart";
import FunnelChart from "../components/chartWidget/funnelChart";
import ForcastPayrollBarChart from "../components/forcastPayrollBarChart";
import { IconCircleCaretUp, IconCoinMonero } from "@tabler/icons";

import { useAuthUser } from "react-auth-kit";
import { useServerApi } from "../hooks/userServerApi";
import { PeriodFilter } from "../components/periodFilter";
import { useFormatter } from "../hooks/useFomatter";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import ReactJson from "react-json-view";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import CardStat from "../components/cardStat";
import OrderStatsTable from "../components/orderStatsTable";
import {
  DashboardGiOutstandingConsultantTab,
  DashboardGiOutstandingInsurerTab,
} from "../components/dashboardGiOverdueTab";
import DashboardSalesPerformanceTab from "../components/dashboardSalesPerformanceTab";
import DashboardYearComparisonTab from "../components/dashboardYearComparisonTab";
import DashboardTrChannelPerformanceTab from "../components/dashboardTrChannelPerformanceTab";
import DashboardGiRenewalTab from "../components/dashboardGiRenewalTab";

const DEFAULT_CHECKED_STATUSES = [
  "INF",
  "SUB",
  "RCV",
  "DFT",
  "EXP",
  "AIP",
  "AIP_EXP",
];

const LAZY_TAB_IDS = ["yearComparison", "trChannelPerformance", "giRenewal"];

function DashboardManagement({ role, numQuarters = 10 }) {
  const auth = useAuthUser();
  const [api] = useServerApi();

  const [period, setPeriod] = useState(null);
  const [user, setUser] = useState(null);
  const [productTypeDataByCase, setproductTypeDataByCase] = useState([]);
  const [productTypeDataByFYP, setproductTypeDataByFYP] = useState([]);
  const [productTypeDataByFYC, setproductTypeDataByFYC] = useState([]);

  const [productCatPieData, setproductCatPieData] = useState([]);

  const [productProviderDataByCase, setproductProviderDataByCase] = useState(
    [],
  );
  const [productProviderDataByFYP, setproductProviderDataByFYP] = useState([]);
  const [productProviderDataByFYC, setproductProviderDataByFYC] = useState([]);

  const [orderStatusPieData, setOrderStatusPieData] = useState([]);
  const [orderTypePieData, setOrderTypePieData] = useState([]);

  const [orderStats, setOrderStats] = useState();
  const [rawOrderProductStats, setRawOrderProductStats] = useState({});
  const [giOrderTypeStats, setGiOrderTypeStats] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState({});
  const [yearComparison, setYearComparison] = useState({ years: [], data: [] });
  const [trChannelPerformance, setTrChannelPerformance] = useState({
    summary: { totalPolicies: 0 },
    channels: [],
  });
  const [giRenewalDashboard, setGiRenewalDashboard] = useState({
    summary: {
      confirmedPremium: 0,
      totalPremium: 0,
      renewedPolicies: 0,
      totalPolicies: 0,
      conversionRate: 0,
      nonRenewedCases: 0,
    },
    monthly: [],
    insurers: [],
    productTypes: [],
    insurersByPolicy: [],
  });
  const [selectedStatuses, setSelectedStatuses] = useState(
    DEFAULT_CHECKED_STATUSES
  );
  const [formatter] = useFormatter();
  const [dateType, setDateType] = useState("signedDate");
  const [productCategory, setProductCategory] = useState(null);
  const productCategoryId = productCategory?._id;

  const [activeTab, setActiveTab] = useState("salesPerformance");
  const [statsLoading, setStatsLoading] = useState(false);
  const [yearComparisonLoading, setYearComparisonLoading] = useState(false);
  const [trChannelLoading, setTrChannelLoading] = useState(false);
  const [giRenewalLoading, setGiRenewalLoading] = useState(false);

  const statsRequestIdRef = useRef(0);
  const yearComparisonRequestIdRef = useRef(0);
  const trChannelRequestIdRef = useRef(0);
  const giRenewalRequestIdRef = useRef(0);
  const activeTabRef = useRef(activeTab);
  const loadedLazyTabsRef = useRef(new Set());
  const staleLazyTabsRef = useRef(new Set(LAZY_TAB_IDS));

  activeTabRef.current = activeTab;

  const markLazyTabsStale = useCallback(() => {
    staleLazyTabsRef.current = new Set(LAZY_TAB_IDS);
    loadedLazyTabsRef.current = new Set();
  }, []);

  const fetchOrderStats = async () => {
    if (!period || !period.startDate || !period.endDate) return;

    const requestId = ++statsRequestIdRef.current;
    setStatsLoading(true);

    try {
      const data = await api.Order.getStats(
        period,
        user?._id,
        dateType,
        selectedStatuses,
        productCategoryId
      );

      if (requestId !== statsRequestIdRef.current) return;
      if (!data) return;

      setOrderProductStats(data);
      setRawOrderProductStats(data.orderProductStats || {});
      setGiOrderTypeStats(data.giOrderTypeStats || []);
      setOrderStats(data.orderSummary);
      setSalesPerformance(data.salesPerformance || {});
      setOrderStatusPieData(
        (data.orderSummary || [])
          .filter((s) => s._id != "ALL")
          .map((s) => ({
            id: s._id,
            value: s.count,
          }))
      );
    } finally {
      if (requestId === statsRequestIdRef.current) {
        setStatsLoading(false);
      }
    }
  };

  const fetchYearComparison = async () => {
    const requestId = ++yearComparisonRequestIdRef.current;
    setYearComparisonLoading(true);

    try {
      const data = await api.Order.getYearComparisonStats(
        user?._id,
        dateType,
        selectedStatuses,
        undefined,
        productCategoryId
      );

      if (requestId !== yearComparisonRequestIdRef.current) return;

      setYearComparison(data || { years: [], data: [] });
      loadedLazyTabsRef.current.add("yearComparison");
      staleLazyTabsRef.current.delete("yearComparison");
    } finally {
      if (requestId === yearComparisonRequestIdRef.current) {
        setYearComparisonLoading(false);
      }
    }
  };

  const fetchTrChannelPerformance = async () => {
    if (!period || !period.startDate || !period.endDate) return;

    const requestId = ++trChannelRequestIdRef.current;
    setTrChannelLoading(true);

    try {
      const data = await api.Order.getTrChannelPerformanceStats(
        period,
        user?._id,
        dateType,
        selectedStatuses,
        productCategoryId
      );

      if (requestId !== trChannelRequestIdRef.current) return;

      setTrChannelPerformance(
        data || { summary: { totalPolicies: 0 }, channels: [] }
      );
      loadedLazyTabsRef.current.add("trChannelPerformance");
      staleLazyTabsRef.current.delete("trChannelPerformance");
    } finally {
      if (requestId === trChannelRequestIdRef.current) {
        setTrChannelLoading(false);
      }
    }
  };

  const fetchGiRenewalDashboard = async () => {
    if (!period || !period.startDate || !period.endDate) return;

    const requestId = ++giRenewalRequestIdRef.current;
    setGiRenewalLoading(true);

    try {
      const data = await api.Renewal.getGiRenewalDashboardStats(
        period,
        user?._id
      );

      if (requestId !== giRenewalRequestIdRef.current) return;

      setGiRenewalDashboard(
        data || {
          summary: {
            confirmedPremium: 0,
            totalPremium: 0,
            renewedPolicies: 0,
            totalPolicies: 0,
            conversionRate: 0,
            nonRenewedCases: 0,
          },
          monthly: [],
          insurers: [],
        }
      );
      loadedLazyTabsRef.current.add("giRenewal");
      staleLazyTabsRef.current.delete("giRenewal");
    } finally {
      if (requestId === giRenewalRequestIdRef.current) {
        setGiRenewalLoading(false);
      }
    }
  };

  const fetchLazyTab = useCallback(
    (tabId) => {
      if (tabId === "yearComparison") return fetchYearComparison();
      if (tabId === "trChannelPerformance") return fetchTrChannelPerformance();
      if (tabId === "giRenewal") return fetchGiRenewalDashboard();
      return Promise.resolve();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period, user, dateType, selectedStatuses, productCategoryId]
  );

  const ensureLazyTabLoaded = useCallback(
    (tabId) => {
      if (!LAZY_TAB_IDS.includes(tabId)) return;
      const needsFetch =
        staleLazyTabsRef.current.has(tabId) ||
        !loadedLazyTabsRef.current.has(tabId);
      if (needsFetch) {
        fetchLazyTab(tabId);
      }
    },
    [fetchLazyTab]
  );

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    ensureLazyTabLoaded(tabId);
  };

  const handleRefresh = () => {
    fetchOrderStats();
    if (LAZY_TAB_IDS.includes(activeTab)) {
      fetchLazyTab(activeTab);
    }
  };

  const resetStatData = () => {
    // console.log("resetStatData");
    setproductTypeDataByCase([]);
    setproductCatPieData([]);

    setproductProviderDataByCase([]);
    setproductProviderDataByFYP([]);
    setproductProviderDataByFYC([]);

    setOrderStatusPieData([]);
    setOrderTypePieData([]);
  };

  const setOrderProductStats = (data) => {
    const TOP_ITEMS_LIMIT = 40;
    // console.log("setOrderProductStats", data);
    if (!data || _.isEmpty(data.orderProductStats)) return resetStatData();

    let typeByCase =
      data.orderProductStats?.productType
        ?.map((c) => ({
          id: c.name,
          Case: c.count,
        }))
        .sort((a, b) => a.Case - b.Case)
        .slice(-TOP_ITEMS_LIMIT) ?? [];
    typeByCase.push({ id: "", Case: 0 });
    setproductTypeDataByCase(typeByCase);

    let typeByFYP =
      data.orderProductStats?.productType
        ?.map((c) => ({
          id: c.name,
          Amount: c.totalYearPremium,
        }))
        .filter((c) => c.Amount > 0)
        .sort((a, b) => a.Amount - b.Amount)
        .slice(-TOP_ITEMS_LIMIT) ?? [];
    typeByFYP.push({ id: "", Case: 0 });

    setproductTypeDataByFYP(typeByFYP);

    let typeBYFYC =
      data.orderProductStats?.productType
        ?.map((c) => ({
          id: c.name,
          Amount: c.totalYearCommission,
        }))
        .filter((c) => c.Amount > 0)
        .sort((a, b) => a.Amount - b.Amount)
        .slice(-TOP_ITEMS_LIMIT) ?? [];

    typeBYFYC.push({ id: "", Case: 0 });
    setproductTypeDataByFYC(typeBYFYC);

    let providerByCase =
      data.orderProductStats?.productProvider
        ?.map((c) => ({
          id: c.shortName ?? c.code,
          case: c.count,
        }))
        .sort((a, b) => a.case - b.case)
        .slice(-TOP_ITEMS_LIMIT) ?? [];
    providerByCase.push({ id: "", case: 0 });
    setproductProviderDataByCase(providerByCase);

    let providerByFYP =
      data.orderProductStats.productProvider
        ?.map((c) => ({
          id: c.shortName ?? c.code,
          Amount: c.totalYearPremium,
        }))
        .filter((c) => c.Amount > 0)
        .sort((a, b) => a.Amount - b.Amount)
        .slice(-TOP_ITEMS_LIMIT) ?? [];
    providerByFYP.push({ id: "", case: 0 });
    setproductProviderDataByFYP(providerByFYP);

    let providerByFYC =
      data.orderProductStats.productProvider
        ?.map((c) => ({
          id: c.shortName ?? c.code,
          Amount: c.totalYearCommission,
        }))
        .filter((c) => c.Amount > 0)
        .sort((a, b) => a.Amount - b.Amount)
        .slice(-TOP_ITEMS_LIMIT) ?? [];
    providerByFYC.push({ id: "", case: 0 });
    setproductProviderDataByFYC(providerByFYC);

    setproductCatPieData(
      data.orderProductStats.productCategory?.map((c) => ({
        id: c.code,
        value: c.count,
      })),
    );
  };

  useDeepCompareEffect(() => {
    fetchOrderStats();
    markLazyTabsStale();
    ensureLazyTabLoaded(activeTabRef.current);
  }, [period, user, dateType, selectedStatuses, productCategoryId]);

  const lineChartProps = {
    axisBottom: {
      legend: "Date",
    },
    axisLeft: {
      legend: "Number of Orders",
    },
  };

  const lineMapper = (result) => [
    {
      id: "Orders",
      data: result ? result?.map((r) => ({ x: r._id, y: r.count })) : [],
    },
  ];

  let numFormatter = Intl.NumberFormat("en", { notation: "compact" });

  const renderProductDashboardWidget = () => (
    <SimpleGrid cols={1} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mt="50px">
      <BarChart
        title={"Product Providers (Case)"}
        height={900}
        description={"The number of order based on order types within quaters."}
        data={productProviderDataByCase}
        chartProps={{
          keys: ["case"],
          layout: "horizontal",
          indexBy: "id",
          margin: { top: 50, right: 130, bottom: 50, left: 150 },
          axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) =>
              value.length > 20 ? value.substring(0, 20) + "..." : value,
          },
        }}
      />

      <BarChart
        title={"Product Types (Case)"}
        height={900}
        description={"The number of order based on order types within quaters."}
        data={productTypeDataByCase}
        chartProps={{
          keys: ["Case"],
          layout: "horizontal",
          indexBy: "id",
          margin: { top: 50, right: 130, bottom: 50, left: 150 },
          axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) =>
              value.length > 20 ? value.substring(0, 20) + "..." : value,
          },
        }}
      />

      <BarChart
        title={"Product Providers (FY Commission)"}
        height={900}
        description={"The number of order based on order types within quaters."}
        data={productProviderDataByFYC}
        chartProps={{
          keys: ["Amount"],
          layout: "horizontal",
          indexBy: "id",
          margin: { top: 50, right: 130, bottom: 50, left: 150 },
          axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) =>
              value.length > 20 ? value.substring(0, 20) + "..." : value,
          },
          axisBottom: {
            format: (value) => `${numFormatter.format(Number(value))}`,
          },
          valueFormat: (value) => `${numFormatter.format(Number(value))}`,
        }}
      />

      <BarChart
        title={"Product Types (FY Commission)"}
        height={900}
        description={"The number of order based on order types within quaters."}
        data={productTypeDataByFYC}
        chartProps={{
          keys: ["Amount"],
          layout: "horizontal",
          indexBy: "id",
          valueFormat: (value) => `${numFormatter.format(Number(value))}`,

          margin: { top: 50, right: 130, bottom: 50, left: 150 },
          axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) =>
              value.length > 20 ? value.substring(0, 20) + "..." : value,
          },
          axisBottom: {
            format: (value) => `${numFormatter.format(Number(value))}`,
          },
        }}
      />

      <BarChart
        title={"Product Providers (FY Premium)"}
        height={900}
        description={"The number of order based on order types within quaters."}
        data={productProviderDataByFYP}
        chartProps={{
          keys: ["Amount"],
          layout: "horizontal",

          indexBy: "id",
          margin: { top: 50, right: 130, bottom: 50, left: 150 },
          valueFormat: (value) => `${numFormatter.format(Number(value))}`,

          axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          },
          axisBottom: {
            format: (value) => `${numFormatter.format(Number(value))}`,
          },
        }}
      />

      <BarChart
        title={"Product Types (FY Premium)"}
        height={900}
        description={"The number of order based on order types within quaters."}
        data={productTypeDataByFYP}
        chartProps={{
          keys: ["Amount"],
          layout: "horizontal",
          valueFormat: (value) => `${numFormatter.format(Number(value))}`,
          indexBy: "id",
          margin: { top: 50, right: 130, bottom: 50, left: 150 },
          axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) =>
              value.length > 20 ? value.substring(0, 20) + "..." : value,
          },
          axisBottom: {
            format: (value) => `${numFormatter.format(Number(value))}`,
          },
        }}
      />
    </SimpleGrid>
  );

  const renderOrderDashboardWidget = () => (
    <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mt="50px">
      <PieChart
        title={"Order Status"}
        staticData={orderStatusPieData}
        height={350}
      />

      <PieChart
        title={"Category"}
        staticData={productCatPieData}
        height={350}
      />
    </SimpleGrid>
  );

  const { t } = useTranslation();
  return (
    <>
      {/* <ReactJson src={period} style={{ background: "white" }}></ReactJson> */}

      {/* <PeriodFilter
        handleRefresh={() => fetchOrderStats()}
        handlePeriodChange={(p) => setPeriod(p)}
        onDateTypeChange={(t) => setDateType(t)}
        numPeriods={numQuarters}
      /> */}
      <OrderStatsTable
        onPeriodRefresh={handleRefresh}
        onPeriodChange={(p) => setPeriod(p)}
        onUserChange={(u) => setUser(u)}
        onDateTypeChange={(t) => setDateType(t)}
        onProductCategoryChange={(category) => setProductCategory(category)}
        withProductCategoryFilter={true}
        onSelectionChange={(statuses) => setSelectedStatuses(statuses)}
        periodBy="Y"
        numPeriods={numQuarters}
        data={orderStats}
        loading={statsLoading}
        mt="50px"
        highlightOnHover
        withTableBorder
        remark="The order stats are based on the order signed date. (Life) Not included transfer-in business."
      />

      <Tabs
        value={activeTab}
        onTabChange={handleTabChange}
        keepMounted={false}
        mt="100px"
      >
        <Tabs.List>
          <Tabs.Tab value="salesPerformance">
            {t("dashboard.salesPerformance.tab", "Sales Performance")}
          </Tabs.Tab>
          <Tabs.Tab value="yearComparison">
            {t("dashboard.yearComparison.tab", "Year Comparison")}
          </Tabs.Tab>
          <Tabs.Tab value="trChannelPerformance">
            {t(
              "dashboard.trChannelPerformance.tab",
              "TR / Channel Performance"
            )}
          </Tabs.Tab>
          <Tabs.Tab value="giRenewal">
            {t("dashboard.giRenewal.tab", "GI Renewal")}
          </Tabs.Tab>
          <Tabs.Tab value="giOutstandingInsurer">
            {t("dashboard.giOutstanding.insurer", "GI Outstanding - Insurer")}
          </Tabs.Tab>
          <Tabs.Tab value="giOutstandingConsultant">
            {t(
              "dashboard.giOutstanding.consultant",
              "GI Outstanding - TR"
            )}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="salesPerformance">
          <DashboardSalesPerformanceTab
            orderSummary={orderStats}
            orderProductStats={rawOrderProductStats}
            giOrderTypeStats={giOrderTypeStats}
            salesPerformance={salesPerformance}
            selectedStatuses={selectedStatuses}
            loading={statsLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="yearComparison">
          <DashboardYearComparisonTab
            yearComparison={yearComparison}
            loading={yearComparisonLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="trChannelPerformance">
          <DashboardTrChannelPerformanceTab
            trChannelPerformance={trChannelPerformance}
            loading={trChannelLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="giRenewal">
          <DashboardGiRenewalTab
            giRenewalDashboard={giRenewalDashboard}
            loading={giRenewalLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="giOutstandingInsurer">
          <DashboardGiOutstandingInsurerTab />
        </Tabs.Panel>
        <Tabs.Panel value="giOutstandingConsultant">
          <DashboardGiOutstandingConsultantTab />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

export default DashboardManagement;
